import React, { useContext, useEffect, useMemo, useState } from 'react';
import api from '~/services/api';
import { GeneralContext, ModalContext } from '~/contexts';
import './styles.css';

type AnyObj = Record<string, any>;

function pickFirst(obj: AnyObj | null | undefined, keys: string[]) {
    if (!obj) return undefined;
    for (const key of keys) {
        const val = obj[key];
        if (val !== undefined && val !== null && `${val}`.trim() !== '') return val;
    }
    return undefined;
}

function resolveIdentificationCode(unit?: AnyObj, explicit?: string) {
    const candidates = [
        explicit,
        unit?.identificationCode,
        unit?.identification_code,
        unit?.meter, // fallback (se sua Unit não tiver identificationCode direto)
    ].filter(Boolean);

    if (candidates.length === 0) return null;
    return String(candidates[0]);
}


function extractRows(respData: any): AnyObj[] {
    if (Array.isArray(respData)) return respData;
    if (respData && typeof respData === 'object') {
        if (Array.isArray(respData.measurements)) return respData.measurements;
        if (Array.isArray(respData.data)) return respData.data;
        // tenta achar a primeira propriedade array que pareça tabela
        const firstArray = Object.values(respData).find(v => Array.isArray(v));
        if (Array.isArray(firstArray)) return firstArray as AnyObj[];
    }
    return [];
}

function parseYear(row: AnyObj): number | null {
    const y = pickFirst(row, ['year', 'ano']);
    if (y !== undefined) {
        const n = Number(y);
        return Number.isFinite(n) ? n : null;
    }
    const ref = pickFirst(row, ['period', 'yearMonth', 'year_month', 'competencia', 'reference', 'ref', 'date']);
    if (!ref) return null;
    const s = String(ref);
    const m = s.match(/(\d{4})/);
    return m ? Number(m[1]) : null;
}

function fmt(v: any) {
    if (v === undefined || v === null || v === '') return '-';
    if (typeof v === 'number' && Number.isFinite(v)) return v.toLocaleString('pt-BR');
    return String(v);
}

interface Props {
    unit?: AnyObj;
    identificationCode?: string;
}

const InfoMeasurement: React.FC<Props> = ({ unit, identificationCode }) => {
    const { user } = useContext(GeneralContext);
    const Modal = useContext(ModalContext);

    const code = useMemo(() => resolveIdentificationCode(unit, identificationCode), [unit, identificationCode]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [raw, setRaw] = useState<any>(null);
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const [selectedYear, setSelectedYear] = useState<number>(currentYear);

    const rows = useMemo(() => extractRows(raw), [raw]);
    const years = useMemo(() => {
        const set = new Set<number>();
        rows.forEach(r => {
            const y = parseYear(r);
            if (y) set.add(y);
        });
        return Array.from(set).sort((a, b) => b - a);
    }, [rows]);

    const yearOptions = useMemo(() => {
        const set = new Set<number>([currentYear, ...years]);
        return Array.from(set).sort((a, b) => b - a);
    }, [years, currentYear]);

    const filteredRows = useMemo(() => {
        return rows.filter(r => parseYear(r) === selectedYear);
    }, [rows, selectedYear]);

    useEffect(() => {
        // garante que o ano selecionado sempre exista nas opções (evita mismatch do select)
        if (yearOptions.length > 0 && !yearOptions.includes(selectedYear)) {
            setSelectedYear(yearOptions[0]); // ano mais recente disponível
        }
    }, [yearOptions, selectedYear]);

    async function fetchMeasurements() {
        if (!code) return;
        setLoading(true);
        setError('');
        
        const identificationCode = unit?.provider?.energy_utility_code ? `${code}:${unit.provider.energy_utility_code}` : code;
        try {
            const resp = await api.get(`/measurements`, {
                headers: { Authorization: user.token },
                params: {
                    identificationCode: identificationCode,
                }
            });
            setRaw(resp.data);
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Não foi possível carregar as medições. Tente novamente.';
            setError(String(msg));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        // reset ao trocar unidade/código
        setRaw(null);
        setSelectedYear(currentYear);
        setError('');

        if (!code) return;
        fetchMeasurements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    const unitName = unit?.name ? String(unit.name) : '';

    return (
        <div className="infoMeasurement">
            <div className="infoMeasurementHeader">
                <div className="title">
                    <strong>{unitName ? unitName : 'Unidade'}</strong>
                    <span className="meta">Código: { unit?.provider?.energy_utility_code ? `${code}:${unit.provider.energy_utility_code}` : code ? code : 'não encontrado nos dados da unidade'}</span>
                </div>

                <div className="actions">
                    <label className="yearSelectLabel">
                        Ano:{' '}
                        <select
                            value={selectedYear}
                            onChange={(e) => {
                                const v = e.target.value;
                                setSelectedYear(Number(v));
                            }}
                            disabled={loading}
                        >
                            {yearOptions.map(y => (
                                <option key={`y_${y}`} value={y}>{y}</option>
                            ))}
                        </select>
                    </label>
                </div>
            </div>

            {!code && (
                <div className="infoMeasurementEmpty">
                    Não encontrei o <strong>identificationCode</strong> dentro dos dados dessa unidade.
                    <br />
                    Se o backend enviar esse campo com outro nome, me diga qual é (ex: <code>identification_code</code>) que eu ajusto aqui.
                </div>
            )}

            {!!error && <div className="infoMeasurementError">{error}</div>}

            <div className="infoMeasurementTableWrap">
                <table className="infoMeasurementTable">
                    <thead>
                        <tr>
                            <th>Período</th>
                            <th>Consumo Medido</th>
                            <th>Projetado</th>
                            <th>Fora Ponta</th>
                            <th>Ponta</th>
                            <th>Horas Consolidadas</th>
                            <th>Horas Faltantes</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={8} className="stateCell">Carregando...</td>
                            </tr>
                        )}

                        {!loading && filteredRows.length === 0 && code && !error && (
                            <tr>
                                <td colSpan={8} className="stateCell">Nenhum dado retornado para esse código.</td>
                            </tr>
                        )}

                        {!loading && filteredRows.map((row, idx) => {
                            const period = pickFirst(row, ['period', 'reference', 'competencia', 'yearMonth', 'year_month', 'date']) ?? idx + 1;
                            const consumoMedido = pickFirst(row, ['consumption', 'consumo', 'consumo_total']);
                            const projectedConsumption = pickFirst(row, ['projectedConsumption', 'projected_consumption', 'projetado', 'projection']);
                            const offPeak = pickFirst(row, ['off_peak_consumption', 'offPeakConsumption', 'off_peak', 'offPeak', 'fora_ponta', 'foraPonta']);
                            const peak = pickFirst(row, ['peak_consumption', 'peakConsumption', 'peak', 'ponta']);
                            const consolidatedHours = pickFirst(row, ['consolidatedHours', 'consolidated_hours', 'horas_consolidadas']);
                            const missingHours = pickFirst(row, ['missingHours', 'missing_hours', 'horas_faltantes']);
                            const status = pickFirst(row, ['status', 'situacao', 'state']);

                            return (
                                <tr key={`m_${idx}`}>
                                    <td>{fmt(period)}</td>
                                    <td>{fmt(consumoMedido)}</td>
                                    <td>{fmt(projectedConsumption)}</td>
                                    <td>{fmt(offPeak)}</td>
                                    <td>{fmt(peak)}</td>
                                    <td>{fmt(consolidatedHours)}</td>
                                    <td>{fmt(missingHours)}</td>
                                    <td>{fmt(status)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <footer>
                <button id="btnCancel" type="button" onClick={() => Modal.setModalOpen(false)} disabled={loading}>
                    Fechar
                </button>
            </footer>
        </div>
    );
};

export default InfoMeasurement;